// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

import type { Translator } from "@unilarva/translator";
import type { TranslatorDomBinding } from "@unilarva/translator/dom";
import type { InitialState } from "../../shared/src/types";
import { renderSong } from "../../shared/src/render";
import { parseViewUrl } from "../../shared/src/view-url";
import type { SongIndexClient } from "./song-index-client";
import { initializeGroupedSongResult } from "./song-result-renderer";

/** Owns exact song lookup while preserving the initially server-rendered card. */
export class SongManager {
  private request = 0;
  private active = false;

  constructor(
    private readonly state: InitialState,
    private readonly translator: Translator,
    private readonly binding: TranslatorDomBinding,
    private readonly index: SongIndexClient,
  ) {}

  /** Activates song content, invalidating pending lookups when navigating away. */
  handleViewChange(_previous: string, next: string): void {
    this.active = next === "song";
    const request = ++this.request;
    if (!this.active) return;
    const pane = document.querySelector<HTMLElement>('[data-sb-pane="song"]');
    if (!pane) return;
    const setContent = (html: string): void => {
      (pane.querySelector<HTMLElement>(".sb-zoom-wrap") ?? pane).innerHTML = html;
    };
    const id = parseViewUrl(new URL(window.location.href)).songId ?? "";
    const root = pane.querySelector<HTMLElement>("[data-sb-song-root]");
    if (root?.dataset.sbSongLoaded === "1" && root.dataset.sbSongId === id) {
      this.initialize(pane);
      return;
    }
    if (!id) {
      setContent(renderSong(this.state, this.translator, { songStatus: "missing-id", songId: "" }));
      this.initialize(pane);
      return;
    }
    // Do not keep the previous song visible while a new exact lookup is pending.
    setContent(renderSong(this.state, this.translator, { songStatus: "loading", songId: id }));
    this.initialize(pane);
    void this.index
      .lookupExactSongId(id)
      .then(result => {
        if (!this.active || request !== this.request) return;
        setContent(
          renderSong(this.state, this.translator, {
            songId: id,
            songGroup: result.groups[0] ?? null,
            songStatus: result.groups.length ? undefined : "not-found",
          }),
        );
        this.initialize(pane);
      })
      .catch(() => {
        if (!this.active || request !== this.request) return;
        setContent(
          renderSong(this.state, this.translator, { songStatus: "unavailable", songId: id }),
        );
        this.initialize(pane);
      });
  }

  /** Refreshes the actual song title after rendering or changing interface language. */
  refreshTitle(): void {
    if (!this.active) return;
    const songTitle = document.querySelector<HTMLElement>(
      '[data-sb-pane="song"] .sb-search-title-text',
    )?.textContent;
    const title = songTitle || this.translator.translateKey("song.title");
    const heading = document.querySelector<HTMLElement>(".sb-title-main");
    if (heading && songTitle) {
      heading.removeAttribute("data-i18n");
      heading.textContent = title;
    }
    const siteTitle =
      this.translator.translateKey("site.title.short").trim() ||
      this.translator.translateKey("site.title");
    document.title = siteTitle ? `${title} — ${siteTitle}` : title;
  }

  /** Hydrates native disclosures without replacing SSR content or duplicating cards. */
  private initialize(pane: HTMLElement): void {
    pane.querySelectorAll<HTMLElement>(".sb-search-hit").forEach(article => {
      initializeGroupedSongResult(article, {
        translator: this.translator,
        books: this.state.books,
      });
    });
    this.binding.update(pane);
    this.refreshTitle();
  }
}
